import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import { ellipsis } from 'polished'
import contextStandardTeaser from '@contexts/standardTeaser'
import { StandardTeaserLayoutContextType } from '@contexts/standardTeaser'
import { TitleProps } from '@widgets/Teaser/standard/types'
import {
  desktopCSS,
  regularMobileCSS,
  smallMobileCSS,
  tabletCSS,
} from '@measures/responsive'

type StyledTitleProps = Pick<
  StandardTeaserLayoutContextType,
  | 'hasVideoOnlyStyle'
  | 'teaserFlex'
  | 'isInMegaModule'
  | 'hasMiniDesktopStyle'
  | 'hasExtraLargeTabletStyle'
  | 'articleType'
  | 'accentColor'
>

const { StandardTeaserLayoutContext } = contextStandardTeaser

const StyledTitle = styled.div<StyledTitleProps>`
  ${({
    theme: {
      fonts: { goodOT: goodOTFontName, goodOTNarrow: goodOTNarrowFontName },
      colors: {
        white,
        grey: { grey_100 },
      },
    },
    hasVideoOnlyStyle,
    teaserFlex,
    isInMegaModule,
    hasMiniDesktopStyle,
    hasExtraLargeTabletStyle,
    articleType,
    accentColor,
  }) => {
    const textDecorationStyle = css`
      text-decoration-line: underline;
      text-decoration-thickness: 3px;
      text-decoration-color: ${accentColor};
    `

    let cssDesktop
    if (teaserFlex === '3') {
      cssDesktop = css`
        font-size: 28px;
        ${ellipsis('100%', 2)};
      `
    } else {
      cssDesktop = css`
        font-size: ${hasMiniDesktopStyle ? '21px' : '28px'};
        ${ellipsis('100%', hasMiniDesktopStyle ? 4 : 3)};
      `
    }

    const cssTablet = css`
      font-size: ${isInMegaModule && !hasExtraLargeTabletStyle
        ? '21px'
        : '28px'};
      ${ellipsis('100%', 3)};
    `

    return css`
      font-family: ${articleType !== 'default'
        ? goodOTFontName
        : goodOTNarrowFontName};
      font-weight: 900;
      line-height: 1.1;
      font-style: ${articleType !== 'default' ? 'italic' : 'normal'};
      color: ${hasVideoOnlyStyle ? white : grey_100};
      ${articleType === 'commentary' && textDecorationStyle}
      > strong {
        color: ${hasVideoOnlyStyle ? white : grey_100};
        font-style: ${articleType !== 'default' ? 'italic' : 'normal'};
        ${articleType === 'commentary' && textDecorationStyle}
        font-weight: 900;
      }
      > p {
        font-style: ${articleType !== 'default' ? 'italic' : 'normal'};
        ${articleType === 'commentary' && textDecorationStyle}
        margin: 0;
      }

      ${desktopCSS(css`
        ${cssDesktop}
      `)}

      ${tabletCSS(css`
        ${cssTablet}
      `)}

      ${smallMobileCSS(css`
        font-size: 21px;
        ${ellipsis('100%', 4)};
      `)}

      ${regularMobileCSS(css`
        font-size: 24px;
        ${ellipsis('100%', 4)};
      `)}
    `
  }}
`

const Title: FunctionComponent<TitleProps> = ({ children }) => {
  const {
    hasVideoOnlyStyle,
    teaserFlex,
    isInMegaModule,
    hasMiniDesktopStyle,
    hasExtraLargeTabletStyle,
    articleType,
    accentColor,
  } = useContext(StandardTeaserLayoutContext)

  return (
    <StyledTitle
      hasVideoOnlyStyle={hasVideoOnlyStyle}
      isInMegaModule={isInMegaModule}
      hasMiniDesktopStyle={hasMiniDesktopStyle}
      hasExtraLargeTabletStyle={hasExtraLargeTabletStyle}
      teaserFlex={teaserFlex}
      articleType={articleType}
      accentColor={accentColor}
      dangerouslySetInnerHTML={{
        __html: children,
      }}
    />
  )
}

export default Title
